<section hlmCard>
  <div hlmCardContent class="space-y-4 p-4">
    <!-- what is still required -->
    <div class="flex items-center gap-2 text-sm">
      @if (missingRequired().length === 0) {
        <ng-icon name="lucideCircleCheck" class="shrink-0 text-green-600" />
        <span>Alle Pflichtelemente auf der Seite</span>
      } @else {
        <ng-icon name="lucideTriangleAlert" class="shrink-0 text-amber-600" />
        <span [hlmTooltip]="'Es fehlt: ' + missingLabels()" class="cursor-help"
          >Pflicht {{ placedRequired() }}/{{ checklist().length }}</span
        >
        <button
          hlmBtn
          variant="outline"
          size="sm"
          type="button"
          class="ms-auto h-7"
          [hlmTooltip]="'Alle fehlenden Pflichtelemente platzieren: ' + missingLabels()"
          (click)="addRequired.emit()">
          <ng-icon name="lucideWandSparkles" />
          Alle platzieren
        </button>
      }
    </div>

    <!-- add -->
    <div>
      <h3 class="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
        Hinzufügen
      </h3>
      <div class="grid grid-cols-3 gap-1.5">
        @for (tile of tiles(); track tile.key) {
          <button
            type="button"
            class="bg-background hover:bg-accent relative flex flex-col items-center justify-center gap-1 rounded-md border px-1 py-2 text-xs"
            [class.border-primary]="tile.action === 'shapes' && shapesOpen()"
            [hlmTooltip]="
              tile.placedId
                ? 'Bereits platziert, auf der Seite anzeigen'
                : tile.hint || 'Hinzufügen: ' + tile.label
            "
            [attr.aria-label]="tile.label"
            (click)="onTile(tile)">
            <ng-icon [name]="tile.icon" size="20" />
            <span>{{ tile.label }}</span>
            @if (tile.placedId) {
              <ng-icon
                name="lucideCircleCheck"
                class="absolute top-1 right-1 text-green-600"
                size="12" />
            } @else if (tile.hint) {
              <ng-icon
                name="lucideTriangleAlert"
                class="absolute top-1 right-1 text-amber-600"
                size="12" />
            }
          </button>
        }
      </div>
      @if (shapesOpen()) {
        <div class="mt-1.5 flex flex-wrap gap-1" role="group" aria-label="Form wählen">
          @for (s of shapeChoices; track s.value) {
            <button
              hlmBtn
              variant="outline"
              size="sm"
              type="button"
              class="h-auto py-1.5"
              [hlmTooltip]="s.label"
              [attr.aria-label]="s.label"
              (click)="onShape(s.value)">
              <ng-icon [name]="s.icon" />
            </button>
          }
        </div>
      }
    </div>

    <!-- values -->
    <details open class="group">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
        <ng-icon name="lucideChevronRight" class="transition-transform group-open:rotate-90" />
        Werte
      </summary>
      @for (group of valueGroups(); track group.label) {
        <p class="text-muted-foreground mt-2 mb-1 text-xs">{{ group.label }}</p>
        <ul class="space-y-0.5">
          @if (group.label === 'Aus deinen Einstellungen' && addressRow(); as address) {
            <!-- the address: one text made of the four parts below -->
            <li class="hover:bg-accent flex items-center gap-1 rounded px-1 text-sm">
              @if (address.placedId; as id) {
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
                  [hlmTooltip]="'In einem Text, auf der Seite anzeigen'"
                  (click)="select.emit(id)">
                  <ng-icon name="lucideCircleCheck" class="shrink-0 text-green-600" size="14" />
                  <span class="text-muted-foreground truncate">{{ address.label }}</span>
                </button>
              } @else {
                <span class="flex min-w-0 flex-1 items-center gap-2 py-1">
                  <span class="truncate">{{ address.label }}</span>
                </span>
              }
              <button
                hlmBtn
                variant="ghost"
                size="icon-sm"
                type="button"
                class="shrink-0"
                [hlmTooltip]="'Straße, PLZ, Ort und Land als ein Text platzieren'"
                aria-label="Adresse platzieren"
                (click)="addText.emit(address.preset)">
                <ng-icon name="lucidePlus" />
              </button>
            </li>
          }
          @for (row of group.rows; track row.placeholder.key) {
            <li class="hover:bg-accent flex items-center gap-1 rounded px-1 text-sm">
              @if (row.placedId; as id) {
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
                  [hlmTooltip]="'In einem Text, auf der Seite anzeigen'"
                  (click)="select.emit(id)">
                  <ng-icon name="lucideCircleCheck" class="shrink-0 text-green-600" size="14" />
                  <span class="text-muted-foreground truncate">{{ row.placeholder.label }}</span>
                </button>
              } @else {
                <span class="flex min-w-0 flex-1 items-center gap-2 py-1">
                  <span class="truncate">{{ row.placeholder.label }}</span>
                  @if (row.required) {
                    <span hlmBadge variant="destructive" class="shrink-0">Pflicht</span>
                  }
                </span>
              }
              @if (valueHint(row); as hint) {
                <ng-icon
                  name="lucideTriangleAlert"
                  class="shrink-0 text-amber-600"
                  size="14"
                  [hlmTooltip]="hint"
                  [attr.aria-label]="hint" />
              }
              <button
                hlmBtn
                variant="ghost"
                size="icon-sm"
                type="button"
                class="shrink-0"
                [hlmTooltip]="'Als eigenen Text platzieren'"
                [attr.aria-label]="row.placeholder.label + ' platzieren'"
                (click)="addText.emit(row.preset)">
                <ng-icon name="lucidePlus" />
              </button>
            </li>
          }
        </ul>
      }
      <p hlmMuted class="mt-2 text-xs">
        Werte füllt der Gutschein oder deine Einstellungen. Sie lassen sich auch in jeden Text
        einfügen: per Doppelklick auf den Text oder im Textfeld rechts.
      </p>
    </details>

    <!-- layers: folded away by default, the page itself shows them -->
    <details class="group">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
        <ng-icon name="lucideChevronRight" class="transition-transform group-open:rotate-90" />
        Ebenen
        <span hlmBadge variant="outline" class="ms-auto">{{ layers().length }}</span>
      </summary>
      <ul class="mt-2 space-y-0.5">
        @for (layer of layers(); track layer.id) {
          <li
            class="hover:bg-accent flex items-center gap-1 rounded px-1 text-sm"
            [class.bg-accent]="layer.selected">
            <button
              type="button"
              class="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
              [attr.aria-current]="layer.selected ? 'true' : null"
              (click)="select.emit(layer.id)">
              <ng-icon [name]="layer.icon" class="text-muted-foreground shrink-0" size="14" />
              <span class="truncate" [class.opacity-50]="layer.muted">{{ layer.title }}</span>
            </button>
            @if (layer.pinned) {
              <ng-icon
                name="lucideLock"
                class="text-muted-foreground shrink-0"
                size="14"
                [hlmTooltip]="'Liegt immer ganz oben'" />
            } @else {
              <button
                hlmBtn
                variant="ghost"
                size="icon-sm"
                type="button"
                class="shrink-0"
                [disabled]="!layer.canFront"
                [hlmTooltip]="'Nach vorne'"
                aria-label="Nach vorne"
                (click)="move.emit({ id: layer.id, delta: 1 })">
                <ng-icon name="lucideChevronUp" />
              </button>
              <button
                hlmBtn
                variant="ghost"
                size="icon-sm"
                type="button"
                class="shrink-0"
                [disabled]="!layer.canBack"
                [hlmTooltip]="'Nach hinten'"
                aria-label="Nach hinten"
                (click)="move.emit({ id: layer.id, delta: -1 })">
                <ng-icon name="lucideChevronDown" />
              </button>
            }
          </li>
        }
      </ul>
      <p hlmMuted class="mt-2 text-xs">
        Oben liegt vorne. Klicken wählt aus. Ob QR-Code und Ablaufdatum Pflicht sind, bestimmen die
        <a class="underline" routerLink="/settings" [queryParams]="{ tab: 'voucher-design' }"
          >Einstellungen</a
        >.
      </p>
    </details>
  </div>
</section>
